import { Link } from "react-router-dom";
import { ArrowRight, Zap, UserPlus, Target, Wand2, Calendar, Send, BarChart3, Github, Twitter, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DEMO_VIDEO = `${BACKEND}/api/static/about-demo.mp4`;

const STEPS = [
  {
    icon: UserPlus,
    num: "01",
    title: "Sign up & connect your site",
    desc: "Create an account in seconds with email or Google. Drop your domain into onboarding — RankPilot spins up your first project workspace.",
    route: "/register",
    cta: "Create account",
  },
  {
    icon: Target,
    num: "02",
    title: "AI Business Analysis & SEO Audit",
    desc: "GPT-5.2 reads your site, profiles your business, and runs a 100-point audit. You'll see your score, prioritized fixes, and quick-win opportunities.",
    route: "/audit",
    cta: "See the audit",
  },
  {
    icon: Calendar,
    num: "03",
    title: "Generate a content plan",
    desc: "Get a multi-week editorial calendar with SEO-aware blog topics, target keywords, and intent — built specifically for your niche.",
    route: "/content-plan",
    cta: "Open content plan",
  },
  {
    icon: Wand2,
    num: "04",
    title: "Auto-write blogs with featured images",
    desc: "Long-form posts complete with meta tags, FAQ schema, internal links, and a Gemini-generated featured image — ready to publish.",
    route: "/blog",
    cta: "Try the blog generator",
  },
  {
    icon: Send,
    num: "05",
    title: "Auto-post & schedule on autopilot",
    desc: "Push new posts to Telegram, Discord, or any webhook. Schedule recurring autopilot runs daily, weekly, or monthly — set it and forget it.",
    route: "/scheduler",
    cta: "Open scheduler",
  },
  {
    icon: BarChart3,
    num: "06",
    title: "Track LLM visibility & rankings",
    desc: "See how your brand shows up across ChatGPT, Claude, and Gemini. Watch keyword positions and AI citations climb over time.",
    route: "/llm-visibility",
    cta: "View LLM tracker",
  },
];

const FAQS = [
  {
    q: "What does RankPilot AI actually do?",
    a: "It's an autopilot SEO platform. You give it a website URL and it: audits the site, builds a multi-week content plan, writes long-form blog posts (with featured images, meta tags, and FAQ schema), and auto-publishes them to channels like Telegram and Discord — on a recurring schedule.",
  },
  {
    q: "Do I need any SEO experience to use it?",
    a: "Nope. The autopilot pipeline runs end-to-end with one click. Every recommendation is written in plain language with the 'why' explained. If you want to dive deeper, every page has manual controls.",
  },
  {
    q: "What AI models power RankPilot?",
    a: "Text generation runs on OpenAI GPT-5.2. Featured images are generated with Gemini's Nano Banana model. The in-app helper chatbot also runs on GPT-5.2.",
  },
  {
    q: "How long until I see ranking improvements?",
    a: "SEO is a compounding game. Most users see indexation lift in 2–4 weeks once content starts shipping consistently, with measurable ranking movement at 6–12 weeks. Faster if your domain has authority already.",
  },
  {
    q: "Can I run multiple websites?",
    a: "Yes — every account supports multiple projects/workspaces. Switch between them from the project picker in the dashboard sidebar.",
  },
  {
    q: "Is my data private?",
    a: "Your project data stays in your workspace. We never train models on your data, and we never publish to your channels without your explicit configuration. Read more on the Privacy page.",
  },
  {
    q: "How much does it cost?",
    a: "We're in MVP. All features are free during this phase. Subscription plans (Starter / Pro / Agency) are coming soon.",
  },
];

export default function AboutPage() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100" data-testid="about-page">
      {/* nav */}
      <header className="sticky top-0 z-30 border-b border-zinc-800/80 bg-[#09090B]/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="about-nav-logo">
            <div className="w-7 h-7 bg-[#0055FF] rounded-sm grid place-items-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-semibold text-lg">RankPilot<span className="text-[#0055FF]"> AI</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/about" className="text-white">How it works</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Button asChild className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="about-nav-app">
                <Link to="/dashboard">Open app <ArrowRight className="w-4 h-4 ml-1" /></Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" className="text-zinc-300 hover:text-white hover:bg-zinc-900 rounded-sm">
                  <Link to="/login">Log in</Link>
                </Button>
                <Button asChild className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="about-nav-signup">
                  <Link to="/register">Start free</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative border-b border-zinc-800/80 overflow-hidden">
        <div className="grain absolute inset-0" />
        <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-16 text-center">
          <Badge variant="outline" className="rounded-sm border-[#0055FF]/40 bg-[#0055FF]/10 text-[#3377FF] px-3 py-1 mb-6">
            <PlayCircle className="w-3 h-3 mr-1.5" /> WATCH THE 12s DEMO
          </Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05]" data-testid="about-title">
            How RankPilot works.
          </h1>
          <p className="mt-6 text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed" data-testid="about-subtitle">
            Six steps from sign-up to "ranking on autopilot." Watch the demo, then jump straight in.
          </p>
        </div>
      </section>

      {/* DEMO VIDEO */}
      <section className="border-b border-zinc-800/80">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="relative rounded-sm overflow-hidden border border-zinc-800 bg-black shadow-[0_0_120px_-20px_#0055FF40]" data-testid="about-video-wrap">
            <video
              src={DEMO_VIDEO}
              controls
              autoPlay
              muted
              loop
              playsInline
              poster=""
              className="w-full aspect-video object-cover bg-black"
              data-testid="about-demo-video"
            >
              Your browser does not support the video tag.
            </video>
          </div>
          <p className="mt-4 text-center text-xs uppercase tracking-[0.2em] text-zinc-600">
            AI-generated product reel · Sora 2
          </p>
        </div>
      </section>

      {/* STEPS */}
      <section className="border-b border-zinc-800/80">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-2xl mb-16">
            <span className="label-eyebrow">The flow</span>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-3 mb-4" data-testid="about-steps-title">
              Six steps to autopilot rankings.
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Each step lives at its own page in the app. You can run the entire pipeline with a single click on the Autopilot page, or trigger any step manually.
            </p>
          </div>
          <ol className="space-y-4">
            {STEPS.map((s, i) => (
              <li key={s.num} className="group relative" data-testid={`about-step-${i + 1}`}>
                <div className="grid lg:grid-cols-12 gap-6 items-start border border-zinc-800/80 rounded-sm bg-zinc-950/60 p-6 lg:p-8 hover:border-zinc-700 transition-colors">
                  <div className="lg:col-span-1 flex lg:block items-center gap-3">
                    <span className="font-mono text-3xl text-[#0055FF]">{s.num}</span>
                  </div>
                  <div className="lg:col-span-1">
                    <div className="w-12 h-12 rounded-sm bg-[#0055FF]/10 border border-[#0055FF]/30 grid place-items-center">
                      <s.icon className="w-5 h-5 text-[#3377FF]" />
                    </div>
                  </div>
                  <div className="lg:col-span-7">
                    <h3 className="font-display text-2xl font-medium tracking-tight">{s.title}</h3>
                    <p className="mt-2 text-zinc-400 leading-relaxed">{s.desc}</p>
                  </div>
                  <div className="lg:col-span-3 lg:text-right">
                    <Button asChild variant="outline" className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm" data-testid={`about-step-cta-${i + 1}`}>
                      <Link to={user ? s.route : "/register"}>
                        {s.cta} <ArrowRight className="w-4 h-4 ml-1.5" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-zinc-800/80">
        <div className="max-w-3xl mx-auto px-6 py-24">
          <div className="text-center mb-12">
            <span className="label-eyebrow">FAQ</span>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-3" data-testid="about-faq-title">
              Frequently asked.
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="about-faq">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border border-zinc-800/80 rounded-sm bg-zinc-950/60 px-5 data-[state=open]:border-zinc-700"
                data-testid={`about-faq-item-${i + 1}`}
              >
                <AccordionTrigger className="text-left text-base font-medium hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-zinc-400 leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-zinc-800/80 relative overflow-hidden">
        <div className="grain absolute inset-0" />
        <div className="relative max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="about-cta-title">
            Ready to ship rankings?
          </h2>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto">
            Spin up your first project in under a minute. No credit card.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button asChild size="lg" className="h-14 px-10 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="about-cta-primary">
              <Link to={user ? "/dashboard" : "/register"}>
                {user ? "Open dashboard" : "Get started free"} <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-14 px-10 bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm" data-testid="about-cta-secondary">
              <Link to="/">Back to home</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#09090B] py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-[#0055FF] rounded-sm grid place-items-center">
                <Zap className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="font-display font-medium">RankPilot AI</span>
              <span className="text-zinc-600 text-xs ml-3">© 2026 — All rights reserved</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-zinc-500">
              <Link to="/privacy" className="hover:text-zinc-300 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-zinc-300 transition-colors">Terms</Link>
              <a href="#" className="hover:text-zinc-300 transition-colors" aria-label="Twitter"><Twitter className="w-4 h-4" /></a>
              <a href="#" className="hover:text-zinc-300 transition-colors" aria-label="GitHub"><Github className="w-4 h-4" /></a>
            </div>
          </div>
          <div className="border-t border-zinc-900 pt-4 text-xs text-zinc-600 flex flex-col md:flex-row justify-between gap-2" data-testid="about-business-info">
            <span><strong className="text-zinc-500">Oz Tech Hub</strong> · 17 Selma Walk, Donnybrook, VIC 3064, Australia</span>
            <span>+61 413 227 784 · <a href="mailto:support@myrankpilotai.com" className="hover:text-zinc-400">support@myrankpilotai.com</a></span>
          </div>
        </div>
      </footer>
    </div>
  );
}
